<template>
  <DashboardContainer :pageTitle="pageTitle">
    <GivarooSpinner v-if="loading" class="mt-24" />

    <template v-else>
      <div class="leaderboard-shell">
        <section class="leaderboard-toolbar">
          <div class="leaderboard-toolbar__controls">
            <div class="filter-field">
              <label for="leaderboard-date" class="filter-field-label">Date</label>
              <FlatPickr
                id="leaderboard-date"
                name="date"
                class="input--text--small w-full placeholder:text-gray-400"
                placeholder="Select date"
                :value="selectedDate"
                @input="handleDateChange($event.target.value)"
              />
            </div>
            <div class="filter-field">
              <span class="filter-field-label">View type</span>
              <div class="leaderboard-type-tabs">
                <button
                  type="button"
                  class="leaderboard-type-tab"
                  :class="{ 'leaderboard-type-tab--active': leaderboardType === 'individual' }"
                  @click="setLeaderboardType('individual')"
                >
                  Individual
                </button>
                <button
                  type="button"
                  class="leaderboard-type-tab"
                  :class="{ 'leaderboard-type-tab--active': leaderboardType === 'campaign' }"
                  @click="setLeaderboardType('campaign')"
                >
                  Campaign
                </button>
              </div>
            </div>
          </div>
          <p class="text-sm text-gray-500 sm:max-w-xs sm:text-right">
            Rankings for
            <span class="font-medium text-gray-700">{{ formattedSelectedDate }}</span>
          </p>
        </section>

        <div class="leaderboard-stats">
          <div class="leaderboard-stat-card">
            <p class="leaderboard-stat-card__label">Daily leader</p>
            <p class="leaderboard-stat-card__value">{{ dailyLeaderName }}</p>
          </div>
          <div class="leaderboard-stat-card">
            <p class="leaderboard-stat-card__label">Daily signups</p>
            <p class="leaderboard-stat-card__value">{{ dailyTopSignups }}</p>
          </div>
          <div class="leaderboard-stat-card">
            <p class="leaderboard-stat-card__label">Weekly leader</p>
            <p class="leaderboard-stat-card__value">{{ weeklyLeaderName }}</p>
          </div>
          <div class="leaderboard-stat-card">
            <p class="leaderboard-stat-card__label">Weekly signups</p>
            <p class="leaderboard-stat-card__value">{{ weeklyTopSignups }}</p>
          </div>
        </div>

        <nav class="leaderboard-period-tabs" aria-label="Leaderboard period">
          <button
            type="button"
            class="leaderboard-period-tab"
            :class="{ 'leaderboard-period-tab--active': activePeriod === 'daily' }"
            @click="activePeriod = 'daily'"
          >
            <CalendarDaysIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
            Daily
            <span class="leaderboard-period-badge">{{ dailyLeaderboardData.length }}</span>
          </button>
          <button
            type="button"
            class="leaderboard-period-tab"
            :class="{ 'leaderboard-period-tab--active': activePeriod === 'weekly' }"
            @click="activePeriod = 'weekly'"
          >
            <ChartBarIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
            Weekly
            <span class="leaderboard-period-badge">{{ weeklyLeaderboardData.length }}</span>
          </button>
        </nav>

        <section v-show="activePeriod === 'daily'" class="leaderboard-workspace">
          <div class="space-y-3 md:hidden">
            <article v-for="row in dailyLeaderboardData" :key="row.rank" class="campaign-data-card">
              <div class="campaign-data-card__header">
                <div class="flex items-center gap-3 min-w-0">
                  <span :class="rankClass(row.rank)">{{ row.rank }}</span>
                  <div class="min-w-0">
                    <p class="truncate font-medium text-gray-900">
                      {{ rowLabel(row) }}
                    </p>
                    <p class="truncate text-xs text-gray-500">
                      {{ charityLabel(row) }} · {{ locationLabel(row) }}
                    </p>
                  </div>
                </div>
                <span class="leaderboard-signups">{{ row.signups }}</span>
              </div>
            </article>
            <p
              v-if="!dailyLeaderboardData.length"
              class="rounded-xl bg-gray-50 px-4 py-10 text-center text-sm text-gray-500"
            >
              No daily rankings for the selected date.
            </p>
          </div>

          <div class="hidden md:block">
            <BaseTable
              :columns="leaderboardColumns"
              :rows="dailyLeaderboardData"
              row-key="rank"
              empty-text="No daily rankings for the selected date."
            >
              <template #cell-rank="{ row }">
                <span :class="rankClass(row.rank)">{{ row.rank }}</span>
              </template>
              <template #cell-fundraiser="{ row }">{{ row.fundraiser_name }}</template>
              <template #cell-charity="{ row }">{{ charityLabel(row) }}</template>
              <template #cell-location="{ row }">{{ locationLabel(row) }}</template>
              <template #cell-signups="{ row }">
                <span class="leaderboard-signups">{{ row.signups }}</span>
              </template>
            </BaseTable>
          </div>
        </section>

        <section v-show="activePeriod === 'weekly'" class="leaderboard-workspace">
          <div class="space-y-3 md:hidden">
            <article
              v-for="row in weeklyLeaderboardData"
              :key="row.rank"
              class="campaign-data-card"
            >
              <div class="campaign-data-card__header">
                <div class="flex items-center gap-3 min-w-0">
                  <span :class="rankClass(row.rank)">{{ row.rank }}</span>
                  <div class="min-w-0">
                    <p class="truncate font-medium text-gray-900">
                      {{ rowLabel(row) }}
                    </p>
                    <p class="truncate text-xs text-gray-500">
                      {{ charityLabel(row) }} · {{ locationLabel(row) }}
                    </p>
                  </div>
                </div>
                <span class="leaderboard-signups">{{ row.signups }}</span>
              </div>
            </article>
            <p
              v-if="!weeklyLeaderboardData.length"
              class="rounded-xl bg-gray-50 px-4 py-10 text-center text-sm text-gray-500"
            >
              No weekly rankings for the selected week.
            </p>
          </div>

          <div class="hidden md:block">
            <BaseTable
              :columns="leaderboardColumns"
              :rows="weeklyLeaderboardData"
              row-key="rank"
              empty-text="No weekly rankings for the selected week."
            >
              <template #cell-rank="{ row }">
                <span :class="rankClass(row.rank)">{{ row.rank }}</span>
              </template>
              <template #cell-fundraiser="{ row }">{{ row.fundraiser_name }}</template>
              <template #cell-charity="{ row }">{{ charityLabel(row) }}</template>
              <template #cell-location="{ row }">{{ locationLabel(row) }}</template>
              <template #cell-signups="{ row }">
                <span class="leaderboard-signups">{{ row.signups }}</span>
              </template>
            </BaseTable>
          </div>
        </section>

        <section class="mt-10 space-y-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-900">Record holders</h2>
            <p class="text-sm text-gray-500">
              Fundraisers who unlocked Campaign or Home Office record-break achievements.
            </p>
          </div>

          <nav class="leaderboard-period-tabs" aria-label="Record scope">
            <button
              type="button"
              class="leaderboard-period-tab"
              :class="{ 'leaderboard-period-tab--active': recordScope === 'campaigns' }"
              @click="recordScope = 'campaigns'"
            >
              Campaigns
              <span class="leaderboard-period-badge">{{
                recordData.campaigns?.[recordPeriod]?.length || 0
              }}</span>
            </button>
            <button
              type="button"
              class="leaderboard-period-tab"
              :class="{ 'leaderboard-period-tab--active': recordScope === 'home_offices' }"
              @click="recordScope = 'home_offices'"
            >
              Home offices
              <span class="leaderboard-period-badge">{{
                recordData.home_offices?.[recordPeriod]?.length || 0
              }}</span>
            </button>
          </nav>

          <nav class="leaderboard-period-tabs" aria-label="Record period">
            <button
              type="button"
              class="leaderboard-period-tab"
              :class="{ 'leaderboard-period-tab--active': recordPeriod === 'daily' }"
              @click="recordPeriod = 'daily'"
            >
              Daily records
            </button>
            <button
              type="button"
              class="leaderboard-period-tab"
              :class="{ 'leaderboard-period-tab--active': recordPeriod === 'weekly' }"
              @click="recordPeriod = 'weekly'"
            >
              Weekly records
            </button>
          </nav>

          <div class="space-y-3 md:hidden">
            <article v-for="row in activeRecordRows" :key="row.rowKey" class="campaign-data-card">
              <div class="campaign-data-card__header">
                <div class="min-w-0">
                  <p class="truncate font-medium text-gray-900">{{ row.scopeLabel }}</p>
                  <p class="truncate text-xs text-gray-500">
                    {{ row.fundraiser_name }} · {{ formatRecordDate(row.date_broken) }}
                  </p>
                </div>
                <span class="leaderboard-signups">{{ row.signups }}</span>
              </div>
            </article>
            <p
              v-if="!activeRecordRows.length"
              class="rounded-xl bg-gray-50 px-4 py-10 text-center text-sm text-gray-500"
            >
              No records yet for this view.
            </p>
          </div>

          <div class="hidden md:block">
            <BaseTable
              :columns="recordColumns"
              :rows="activeRecordRows"
              row-key="rowKey"
              empty-text="No records yet for this view."
            >
              <template #cell-scope="{ row }">{{ row.scopeLabel }}</template>
              <template #cell-fundraiser="{ row }">{{ row.fundraiser_name }}</template>
              <template #cell-signups="{ row }">
                <span class="leaderboard-signups">{{ row.signups }}</span>
              </template>
              <template #cell-date_broken="{ row }">{{
                formatRecordDate(row.date_broken)
              }}</template>
            </BaseTable>
          </div>
        </section>
      </div>
    </template>
  </DashboardContainer>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import DashboardContainer from '@/components/DashboardContainer.vue'
import GivarooSpinner from '@/components/GivarooSpinner.vue'
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { DonationApi } from '@/services/donations/'
import { CalendarDaysIcon, ChartBarIcon } from '@heroicons/vue/24/outline'

const emptyRecordData = () => ({
  campaigns: { daily: [], weekly: [] },
  home_offices: { daily: [], weekly: [] },
})

export default {
  name: 'Leaderboard',
  components: {
    DashboardContainer,
    GivarooSpinner,
    FlatPickr,
    CalendarDaysIcon,
    ChartBarIcon,
  },
  setup() {
    const pageTitle = ref('Leaderboard')
    const loading = ref(false)
    const activePeriod = ref('daily')
    const leaderboardType = ref('individual')
    const dailyLeaderboardData = ref([])
    const weeklyLeaderboardData = ref([])
    const selectedDate = ref(new Date().toISOString().split('T')[0])
    const recordScope = ref('campaigns')
    const recordPeriod = ref('daily')
    const recordData = ref(emptyRecordData())

    const leaderboardColumns = computed(() => [
      { key: 'rank', label: 'Rank', align: 'center', minWidth: '5rem' },
      ...(leaderboardType.value === 'individual'
        ? [{ key: 'fundraiser', label: 'Fundraiser', minWidth: '12rem' }]
        : [{ key: 'charity', label: 'Campaign', minWidth: '12rem' }]),
      ...(leaderboardType.value === 'individual'
        ? [{ key: 'charity', label: 'Charity', minWidth: '12rem' }]
        : []),
      { key: 'location', label: 'Location', minWidth: '10rem' },
      { key: 'signups', label: 'Signups', align: 'center', minWidth: '7rem' },
    ])

    const recordColumns = computed(() => [
      {
        key: 'scope',
        label: recordScope.value === 'campaigns' ? 'Campaign' : 'Home Office',
        minWidth: '14rem',
      },
      { key: 'fundraiser', label: 'Fundraiser', minWidth: '12rem' },
      { key: 'signups', label: 'Signups', align: 'center', minWidth: '7rem' },
      { key: 'date_broken', label: 'Date Broken', minWidth: '10rem' },
    ])

    const activeRecordRows = computed(() => {
      const rows = recordData.value?.[recordScope.value]?.[recordPeriod.value] || []
      return rows.map((row, index) => ({
        ...row,
        rowKey: `${row.fundraiser_id}-${row.date_broken}-${index}`,
        scopeLabel: recordScope.value === 'campaigns' ? row.campaign : row.home_office,
      }))
    })

    const formattedSelectedDate = computed(() => {
      if (!selectedDate.value) return 'selected date'
      const parsed = new Date(`${selectedDate.value}T12:00:00`)
      if (Number.isNaN(parsed.getTime())) return selectedDate.value
      return parsed.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    })

    const dailyTopSignups = computed(() => dailyLeaderboardData.value[0]?.signups ?? '—')
    const weeklyTopSignups = computed(() => weeklyLeaderboardData.value[0]?.signups ?? '—')

    const dailyLeaderName = computed(() => {
      const row = dailyLeaderboardData.value[0]
      if (!row) return '—'
      return rowLabel(row)
    })

    const weeklyLeaderName = computed(() => {
      const row = weeklyLeaderboardData.value[0]
      if (!row) return '—'
      return rowLabel(row)
    })

    function rowLabel(row) {
      if (leaderboardType.value === 'individual') {
        return row.fundraiser_name || '—'
      }
      return row.npo_name || '—'
    }

    function charityLabel(row) {
      return leaderboardType.value === 'individual' ? row.npo_names || '—' : row.npo_name || '—'
    }

    function locationLabel(row) {
      return leaderboardType.value === 'individual' ? row.locations || '—' : row.location || '—'
    }

    function rankClass(rank) {
      if (rank === 1) return 'leaderboard-rank leaderboard-rank--top'
      if (rank === 2) return 'leaderboard-rank leaderboard-rank--second'
      if (rank === 3) return 'leaderboard-rank leaderboard-rank--third'
      return 'leaderboard-rank leaderboard-rank--default'
    }

    function formatRecordDate(isoDate) {
      if (!isoDate) return '—'
      const parsed = isoDate.includes('T') ? new Date(isoDate) : new Date(`${isoDate}T12:00:00`)
      if (Number.isNaN(parsed.getTime())) return isoDate
      return parsed.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    }

    const handleDateChange = (value) => {
      selectedDate.value = value
      fetchLeaderboardData()
    }

    const fetchLeaderboardData = async () => {
      loading.value = true
      try {
        const dailyResponse = await DonationApi.csc.leaderboard({
          type: leaderboardType.value,
          period: 'daily',
          date: selectedDate.value,
        })
        dailyLeaderboardData.value = (dailyResponse || []).slice(0, 10)

        const weeklyResponse = await DonationApi.csc.leaderboard({
          type: leaderboardType.value,
          period: 'weekly',
          date: selectedDate.value,
        })
        weeklyLeaderboardData.value = (weeklyResponse || []).slice(0, 10)
      } catch (error) {
        console.error('Error fetching leaderboard data:', error)
        dailyLeaderboardData.value = []
        weeklyLeaderboardData.value = []
      } finally {
        loading.value = false
      }
    }

    const fetchRecordHolders = async () => {
      try {
        const data = await DonationApi.csc.leaderboardRecords()
        recordData.value = data || emptyRecordData()
      } catch (error) {
        console.error('Error fetching record holders:', error)
        recordData.value = emptyRecordData()
      }
    }

    const setLeaderboardType = (type) => {
      leaderboardType.value = type
      nextTick(() => {
        fetchLeaderboardData()
      })
    }

    onMounted(() => {
      fetchLeaderboardData()
      fetchRecordHolders()
    })

    return {
      pageTitle,
      loading,
      activePeriod,
      leaderboardType,
      dailyLeaderboardData,
      weeklyLeaderboardData,
      selectedDate,
      formattedSelectedDate,
      dailyTopSignups,
      weeklyTopSignups,
      dailyLeaderName,
      weeklyLeaderName,
      handleDateChange,
      setLeaderboardType,
      leaderboardColumns,
      rowLabel,
      charityLabel,
      locationLabel,
      rankClass,
      recordScope,
      recordPeriod,
      recordData,
      recordColumns,
      activeRecordRows,
      formatRecordDate,
    }
  },
}
</script>
